import { Box, HStack, Spacer, Tooltip, VStack } from '@/components/sgBootstrapReactWrapper';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  updateTablesPreferences,
  userPreferences
} from '@/store/user/userPreferencesSlice';
import { canDoArrayExport, canSeeRestrictedData, getSelectedEntity } from '@/store/user/userSlice';
import { isDefined, isNotDefined } from '@/utils/assertions';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState, type ComponentProps } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useDebounceCallback } from 'usehooks-ts';
import { ActiveAGGridFiltersDisplay } from './ActiveAGGridFiltersDisplay';
// check the licence of AGGrid when using enterprise modules
import { Button } from '@/components/sgBootstrapReactWrapper';
import type { LegalEntity } from '@/model/LegalEntity';
import { getUserPrefsToastStatus, setUserPrefsToastStatus } from '@/store/toasts/globalToastsSlice';
import 'ag-grid-community';
import { type ColDef, type ColGroupDef, type ColumnState, type ExcelRow, type FilterModel, type GetContextMenuItemsParams, type IRowNode, type MenuItemDef } from 'ag-grid-community';
import 'ag-grid-enterprise';
import 'ag-grid-react';
import { AgGridReact } from 'ag-grid-react';

type AGGridProps<TData> = ComponentProps<typeof AgGridReact<TData>>;

type Props<TData> = Pick<
  AGGridProps<TData>,
  | 'rowData'
  | 'pinnedBottomRowData'
  | 'loading'
  | 'onRowClicked'
  | 'rowHeight'
  | 'headerHeight'
  | 'defaultExcelExportParams'
> & {
  /**
   * identifier used as a prefix for:
   * - "data-e2e" property of subcomponents
   * - filter badges intl label ID (identifier + "." + column name)
   */
  identifier: string;
  /**
   * @returns true if the node is visibile, and false if it is filtered
   * @param query - string to search in the node
   * @param node - AGGrid node which has to match the query to be visible
   */
  matchNodeForQuery?: (query: string, node: IRowNode<TData>) => boolean;
  /**
   * height of the AGGrid
   */
  gridHeigth?: number;

  titleLabelId?: string;

  displayExportButton?: boolean;

  columnDefs?: (ColDef<TData, any> | ColGroupDef<TData>)[]
};

export function AGGridWrapper<TData>(props: Props<TData> & { columnState?: ColumnState[] }) {
  const {
    matchNodeForQuery,
    identifier,
    gridHeigth,
    titleLabelId,
    displayExportButton = true,
    defaultExcelExportParams,
    columnDefs,
    ...passthroughProps
  } = props;

  const appDispatch = useAppDispatch();

  const { $t } = useIntl();
  const gridRef = useRef<AgGridReact<TData>>(null);
  const canExport = useAppSelector(canDoArrayExport)
  const hasRestrictedData = useAppSelector(canSeeRestrictedData)
  const selectedEntity: LegalEntity = useAppSelector(getSelectedEntity)
  const userPrefsGrid = useSelector(userPreferences);
  const savedGridState: ColumnState[] = userPrefsGrid?.grids?.[identifier];
  const userPrefsToastStatus = useAppSelector(getUserPrefsToastStatus)

  const [multiSearchFilter, setMultiSearchFilter] = useState('');
  const [frontFilters, setFrontFilters] = useState<FilterModel>({}) // filters managed by aggrid

  const isExternalFilterPresent = useCallback(
    () => multiSearchFilter.trim().length > 0,
    [multiSearchFilter],
  );
  const doesExternalFilterPass = useCallback(
    (node: IRowNode<TData>): boolean => {
      return matchNodeForQuery?.(multiSearchFilter, node) ?? true;
    },
    [multiSearchFilter, matchNodeForQuery],
  )
  const handleClearRequested = useCallback(async (columnId: string) => {
    await gridRef.current?.api.setColumnFilterModel(columnId, null);
    gridRef.current?.api.onFilterChanged('api');
  }, [])
  const handleClearAllRequested = useCallback(() => {
    gridRef.current?.api.setFilterModel(null);
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: biome cannot see this because it is at intention level: when filter changes we need to tell Ag grid.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [multiSearchFilter]);

  /**
   * Call sizeColumnsToFit with a debounce time (default 300ms)
   * to resize the grid to fit perfectly with available space
   * https://www.ag-grid.com/javascript-data-grid/column-sizing/#size-columns-to-fit
   */
  const sizeToFit = useDebounceCallback(() => {
    gridRef.current?.api.sizeColumnsToFit();
  });
  const onGridReady = useCallback(() => {
    sizeToFit();
  }, [sizeToFit]);

  const hasTitle = isDefined(titleLabelId);
  const hasFilters = Object.entries(frontFilters).length > 0;
  const arrayName = identifier.replaceAll('.', '-')
  const exportName = $t({ id: `array.name.${arrayName}` })

  const onBtExport = useCallback(() => {
    const columnKeys = gridRef.current!.api.getAllDisplayedColumns()
      .filter(col => col.getColId() !== 'actions');
    gridRef.current!.api.exportDataAsExcel({ fileName: exportName, columnKeys });
  }, [exportName])
  const grpCellRenderer = (_data: TData, value: any) => <span className='bg-black bg-opacity-08 fw-bold'>{value}</span>

  const onSaveGridColumnState = () => {
    if (!gridRef?.current?.api) return;
    const columnState = gridRef?.current?.api.getColumnState();
    const updatedColumns = columnState.map((col) => ({
      ...col,
      aggFunc: col.aggFunc?.toString(),
    }))
    appDispatch(setUserPrefsToastStatus('loading'))
    appDispatch(updateTablesPreferences({ gridId: identifier, columnState: updatedColumns }))
  }
  const applyPrefs = () => applyGivenPrefs(savedGridState)
  const applyGivenPrefs = (saved: ColumnState[]) => {
    if (isNotDefined(saved)) return;
    gridRef?.current?.api?.applyColumnState({
      state: Object.values(saved),
      applyOrder: true,
    })
  }
  const resetPrefsToDefault = () => {
    if (isNotDefined(columnDefs)) return;
    const newColState: ColumnState[] = mapToColumnState(columnDefs, savedGridState ? Object.values(savedGridState) : [])
    gridRef?.current?.api.applyColumnState({
      state: newColState,
      applyOrder: true,
    })
  }

  useEffect(() => {
    if (userPrefsToastStatus === undefined) return; // prevents apply prefs on closing the toast
    applyPrefs()
  }, [userPrefsToastStatus])

  const CustomActionSideBarPanelComponent = () => {
    const savedGridState: ColumnState[] = useSelector(userPreferences)?.grids?.[identifier];
    const onClickSavePrefs = useDebounceCallback(onSaveGridColumnState, 500)
    const onClickApplyPrefs = useDebounceCallback(() => applyGivenPrefs(savedGridState), 10)
    const onClickResetPrefs = useDebounceCallback(resetPrefsToDefault, 10)
    return <VStack gap='12px' padding='8px' flex='expand' style={{ height: '100%' }}>
      <VStack gap='8px' >
        <Box>
          <h6>
            <FormattedMessage id='array.userPreferences.title' />
          </h6>
        </Box>
        <Box flex='expand'>
          <Tooltip position='left' contents={<FormattedMessage id='array.userPreferences.save.details' />}>
            <Button onClick={onClickSavePrefs} className="btn btn-default btn-md" size='lg' style={{ width: '100%' }}>
              <FormattedMessage id='array.userPreferences.save' />
            </Button>
          </Tooltip>
        </Box>
        <Box flex='expand'>
          <Tooltip position='left' contents={<FormattedMessage id='array.userPreferences.apply.details' />}>
            <Button onClick={onClickApplyPrefs} className="btn btn-default btn-md" size='lg' style={{ width: '100%' }}
              disabled={isNotDefined(savedGridState)}>
              {isDefined(savedGridState) && <FormattedMessage id='array.userPreferences.apply' />}
              {isNotDefined(savedGridState) && <FormattedMessage id='array.userPreferences.apply.nodata' />}
            </Button>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip position='left' contents={<FormattedMessage id='array.userPreferences.reset.details' />} >
            <Button onClick={onClickResetPrefs} className="btn btn-default btn-md" size='lg' style={{ width: '100%' }}>
              <FormattedMessage id='array.userPreferences.reset' />
            </Button>
          </Tooltip>
        </Box>
      </VStack>
      <Spacer />
      {canExport && displayExportButton && <VStack gap='8px'>
        <Box>
          <h6>
            <FormattedMessage id='array.otherActions.title' />
          </h6>
        </Box>
        <Box>
          <Button onClick={onBtExport} className="btn btn-default btn-md" >
            <FormattedMessage id='array.exportExcel' />
          </Button>
        </Box>
      </VStack>}
    </VStack>
  }

  return (
    <VStack fullHeight data-e2e={identifier} gap='4px'>
      <HStack gap="16px" >
        <HStack gap='16px'>
          {hasTitle && <h4>
            <FormattedMessage id={titleLabelId} />
          </h4>
          }
          {canExport && displayExportButton &&
            <Button onClick={onBtExport} className="btn btn-default btn-md" >
              Export Excel
            </Button>}
        </HStack>

        {hasFilters && (
          <ActiveAGGridFiltersDisplay
            identifier={identifier}
            filters={frontFilters}
            onClearRequested={handleClearRequested}
            onClearAllRequested={handleClearAllRequested}
          />
        )}

        {isDefined(matchNodeForQuery) && (
          <>
            <Spacer />

            <Box flex="min">
              <div className="input-group input-group-md" style={{ width: 351 }}>
                <div className="input-icon-start">
                  <em className="icon">search</em>
                </div>
                <input
                  data-e2e={`grid-${identifier}-search-input`}
                  type="search"
                  className="form-control form-control-md "
                  placeholder={$t({ id: 'root.detailedValuation.searchbarPlaceholder' })}
                  value={multiSearchFilter}
                  onChange={(e) => setMultiSearchFilter(e.target.value)}
                />
              </div>
            </Box>
          </>
        )}
      </HStack>
      <div
        data-e2e={`${identifier}-grid`}
        className={clsx('ag-theme-sg-bootstrap-condensed', {
          'flex-fill': isNotDefined(gridHeigth),
        })}
        style={{ height: gridHeigth }}
      >
        <AgGridReact<TData>
          {...passthroughProps}
          gridOptions={{ cellSelection: hasRestrictedData, }}
          columnDefs={columnDefs}
          onGridReady={onGridReady}
          ref={gridRef}
          suppressAggFuncInHeader // writing '<headerName>' instead of "func(<headerName>)" in header
          suppressCellFocus // avoid having focus outline on cell when clicking inside
          groupDisplayType="custom"
          autoGroupColumnDef={{
            headerName: '',
            cellRendererParams: {
              suppressCount: true,
            },
          }}
          defaultCsvExportParams={{
            fileName: exportName,
          }}
          defaultExcelExportParams={{
            ...defaultExcelExportParams,
            fileName: exportName,
            prependContent: getDisclaimer(hasRestrictedData, selectedEntity),
          }}
          suppressExcelExport={!canExport}
          suppressCsvExport={!canExport}
          suppressContextMenu={!canExport}
          getContextMenuItems={(params: GetContextMenuItemsParams) => {
            const defaultItems = params.defaultItems ?? [];
            return hasRestrictedData && identifier == "home.portfolioOverview" ? defaultItems.filter(
              (item) => item !== 'excelExport' && item !== 'csvExport' && item !== 'export',
            ) : defaultItems;
          }}
          onFirstDataRendered={useDebounceCallback(applyPrefs, 10)} // there is a need to debounce to prevent pbs with react hooks + functions triggering in wrong order
          groupHideOpenParents={true}
          groupSuppressBlankHeader={true}
          groupDefaultExpanded={-1} // all row groups expanded at start
          rowClassRules={{
            'bg-lvl4 fw-bold': (params) => !!params.node.rowPinned,
          }}
          groupRowRenderer={grpCellRenderer}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onFilterChanged={({ api }) => setFrontFilters(api.getFilterModel())}
          enableAdvancedFilter={false}
          groupTotalRow={'bottom'}
          tooltipShowDelay={0}
          tooltipInteraction
          components={{ // lists the custom components we may need in AgGrids
            "customActionsToolPanel": CustomActionSideBarPanelComponent
          }
          }
          sideBar={{
            toolPanels: [
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivotMode: true,
                  suppressColumnFilter: true,
                  suppressColumnSelectAll: true,
                },
              },
              {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
                toolPanelParams: {
                  suppressFilterSearch: true,
                },
              },
              {
                id: "actions",
                labelKey: "actions",
                labelDefault: "Actions",
                iconKey: "save",
                toolPanel: "customActionsToolPanel", // references a component name among those listed in the 'components' attribute of the agGrid
              },
            ],
          }}
        />
      </div>
    </VStack>
  );
}

const getDisclaimer = (isInternalUser: boolean, entity: LegalEntity): ExcelRow[] => {
  const { $t } = useIntl();
  const txt: string[] = $t({ id: `array.disclaimer.${entity}` }).split("<br/>")
  const lines: ExcelRow[] = txt.map(t => {
    return {
      cells: [
        {}, // first cell empty
        { data: { value: t, type: "String", } }
      ]
    }
  })
  lines.push({ cells: [] })
  lines.unshift({ cells: [] })

  return isInternalUser ? [] : lines
}

function mapToColumnState<TData>(defs: ColDef<TData, any>[], saved: ColumnState[]): ColumnState[] {
  const findInSaved = (id: string): ColumnState | undefined => saved.find(c => c.colId === id)
  const getInSavedOrDefault = (col: ColDef<TData, any>): ColumnState | undefined => {
    if (isNotDefined(col.field)) return undefined;
    const withColId = isDefined(col.colId) ? findInSaved(col.colId) : undefined
    if (isDefined(col.colId) && isDefined(withColId)) return withColId
    return findInSaved(col.field) ?? { colId: col.field }
  }
  return defs
    .map(col => getInSavedOrDefault(col))
    .filter(col => isDefined(col))
}